import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Bank, UploadSimple, Trash, Eye, Lock, ToggleLeft, ToggleRight, ArrowLeft, Wallet, QrCode, Money, DownloadSimple, FilePdf } from '@phosphor-icons/react';
import { formatDateTime } from '../lib/utils';

const KIND_ICON = { bank: Bank, qr: QrCode, cash: Money };
const COLOR_CLS = {
  blue: 'from-blue-500 to-blue-600',
  emerald: 'from-emerald-500 to-emerald-600',
  amber: 'from-amber-500 to-amber-600',
  violet: 'from-violet-500 to-violet-600',
  rose: 'from-rose-500 to-rose-600',
  orange: 'from-orange-500 to-orange-600',
  indigo: 'from-indigo-500 to-indigo-600',
  teal: 'from-teal-500 to-teal-600',
};

/* ─────────────────────────────────────────────────────────────
 *  Bank Statements list + admin access manager
 *  Routes:
 *    /bank-statements           → this page
 *    /bank-statements/:id       → BankStatementDetailPage
 * ───────────────────────────────────────────────────────────── */
export default function BankStatementsPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [meta, setMeta] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showAccess, setShowAccess] = useState(false);

  const loadAccounts = useCallback(async () => {
    try { const { data } = await api.get('/bank-statements/accounts'); setAccounts(data || []); }
    catch (err) { /* empty */ }
  }, []);

  const loadStatements = useCallback(async (accountKey) => {
    if (!accountKey) { setItems([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get('/bank-statements', { params: { account_key: accountKey } });
      setItems(data || []);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Load fail'); }
    finally { setLoading(false); }
  }, []);

  const loadAccess = useCallback(async () => {
    try {
      const { data: m } = await api.get('/bank-statements/access/me');
      setMeta(m);
      if (m.can_access) await loadAccounts();
    } catch (err) {
      if (err?.response?.status !== 403) toast.error(err?.response?.data?.detail || 'Access load fail');
    }
  }, [loadAccounts]);

  useEffect(() => { loadAccess(); }, [loadAccess]);
  useEffect(() => { if (selectedAccount) loadStatements(selectedAccount.key); }, [selectedAccount, loadStatements]);

  const onDelete = async (id) => {
    if (!window.confirm('Statement aur uske saare transactions delete ho jayenge. Sure?')) return;
    try { await api.delete(`/bank-statements/${id}`); toast.success('Deleted'); loadStatements(selectedAccount.key); loadAccounts(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Delete fail'); }
  };

  const onDownload = async (id, format = 'xlsx') => {
    try {
      const res = await api.get(`/bank-statements/${id}/download`, { params: { format }, responseType: 'blob' });
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^";]+)"?/i);
      const fname = m ? m[1] : `statement_${id}.${format}`;
      const mime = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement('a');
      a.href = url; a.download = fname; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`📥 ${fname}`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Download fail'); }
  };

  if (!meta) return <div className="p-6 text-sm text-gray-500">Loading…</div>;
  if (!meta.can_access) {
    return (
      <div className="max-w-md mx-auto mt-12 p-6 bg-red-50 border border-red-200 rounded-lg text-center" data-testid="bs-no-access">
        <Lock size={32} className="mx-auto text-red-600 mb-2" weight="duotone" />
        <p className="font-bold text-red-800">Access Denied</p>
        <p className="text-sm text-red-700 mt-1">Bank Statements module ka access nahi hai aapko ({user?.role}).</p>
      </div>
    );
  }

  // Account-picker tile view (default landing)
  if (!selectedAccount) {
    return (
      <div className="space-y-5" data-testid="bank-statements-page">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Bank size={24} weight="duotone" className="text-emerald-700" />
              Bank Statements — Select Account
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">Pehle account choose karo, fir us account ka statement upload / dekho</p>
          </div>
          {meta.can_manage_access && (
            <Button size="sm" variant="outline" onClick={() => setShowAccess(true)} data-testid="bs-access-btn">👥 Manage Access</Button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {accounts.map(a => {
            const Icon = KIND_ICON[a.kind] || Bank;
            const grad = COLOR_CLS[a.color] || COLOR_CLS.blue;
            return (
              <button
                key={a.key}
                onClick={() => setSelectedAccount(a)}
                className={`relative bg-gradient-to-br ${grad} text-white rounded-xl p-5 text-left shadow-md hover:shadow-xl hover:scale-[1.02] transition-all`}
                data-testid={`bs-account-${a.key}`}
              >
                <div className="flex items-start justify-between">
                  <Icon size={32} weight="duotone" />
                  <span className="text-[10px] uppercase tracking-wider bg-white/20 rounded px-2 py-0.5 font-bold">{a.kind}</span>
                </div>
                <div className="mt-4 text-lg font-bold">{a.label}</div>
                <div className="mt-1 text-xs opacity-90">
                  {a.statement_count} statements{a.last_uploaded_at && ` · last: ${new Date(a.last_uploaded_at).toLocaleDateString('en-IN')}`}
                </div>
                {a.last_filename && <div className="mt-1 text-[10px] opacity-75 truncate" title={a.last_filename}>📄 {a.last_filename}</div>}
              </button>
            );
          })}
        </div>

        {meta.can_manage_access && <AccessDialog open={showAccess} onClose={() => setShowAccess(false)} />}
      </div>
    );
  }

  // Per-account statements view
  return (
    <div className="space-y-5" data-testid="bank-statements-page">
      <button onClick={() => setSelectedAccount(null)} className="text-sm text-violet-600 hover:underline flex items-center gap-1" data-testid="bs-back">
        <ArrowLeft size={14} /> Back to all accounts
      </button>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bank size={24} weight="duotone" className="text-emerald-700" />
            {selectedAccount.label}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">{items.length} statements · Account-specific view</p>
        </div>
        <Button size="sm" onClick={() => setShowUpload(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="bs-upload-btn">
          <UploadSimple size={14} className="mr-1" /> Upload to this Account
        </Button>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900">
        💡 <b>Tip:</b> Galti se same file 2 baar upload ho jaye to row ke <Trash size={12} className="inline" /> button se turant remove kar sakte ho. Duplicate transactions auto-flag bhi ho jaate hain.
      </div>

      <Card data-testid="bs-list-card">
        <CardContent className="p-0 overflow-x-auto">
          <Table className="table-dense">
            <TableHeader>
              <TableRow>
                <TableHead>Filename</TableHead>
                <TableHead>Uploaded</TableHead>
                <TableHead className="text-right">Rows</TableHead>
                <TableHead className="text-right">Total Credit</TableHead>
                <TableHead className="text-right">Total Debit</TableHead>
                <TableHead className="text-center">Duplicates</TableHead>
                <TableHead className="text-center">Claimed</TableHead>
                <TableHead className="text-center">Suspense</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={9} className="text-center py-8">Loading…</TableCell></TableRow> :
               items.length === 0 ? <TableRow><TableCell colSpan={9} className="text-center py-8 text-gray-500">Is account ke liye koi statement nahi hai. Upload karein.</TableCell></TableRow> :
               items.map(s => (
                <TableRow key={s.id} data-testid={`bs-row-${s.id}`}>
                  <TableCell className="text-xs font-medium">{s.filename}</TableCell>
                  <TableCell className="text-xs">
                    <div>{formatDateTime(s.uploaded_at)}</div>
                    <div className="text-[10px] text-gray-400">by {s.uploaded_by_name}</div>
                  </TableCell>
                  <TableCell className="text-right text-sm font-bold">{s.total_rows}</TableCell>
                  <TableCell className="text-right text-sm text-emerald-700 font-bold">₹{Math.round(s.total_credit).toLocaleString('en-IN')}</TableCell>
                  <TableCell className="text-right text-sm text-rose-700 font-bold">₹{Math.round(s.total_debit).toLocaleString('en-IN')}</TableCell>
                  <TableCell className="text-center">
                    {s.duplicate_count > 0
                      ? <span className="bg-rose-100 text-rose-800 text-[10px] font-bold rounded px-1.5 py-0.5">⚠️ {s.duplicate_count}</span>
                      : <span className="text-gray-300 text-xs">0</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    <span className={`text-[10px] font-bold rounded px-1.5 py-0.5 ${s.claimed_count > 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'}`}>
                      {s.claimed_count}/{s.total_rows}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    {s.suspense_count > 0
                      ? <span className="bg-amber-100 text-amber-800 text-[10px] font-bold rounded px-1.5 py-0.5">⏳ {s.suspense_count}</span>
                      : <span className="text-gray-300 text-xs">0</span>}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <button onClick={() => nav(`/bank-statements/${s.id}`)} className="p-1 text-violet-600 hover:bg-violet-50 rounded" title="Open" data-testid={`bs-open-${s.id}`}><Eye size={14} /></button>
                      <button onClick={() => onDownload(s.id, 'xlsx')} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded" title="Download Excel with remarks/claims" data-testid={`bs-download-${s.id}`}><DownloadSimple size={14} weight="bold" /></button>
                      <button onClick={() => onDownload(s.id, 'pdf')} className="p-1 text-rose-600 hover:bg-rose-50 rounded" title="Download PDF report" data-testid={`bs-download-pdf-${s.id}`}><FilePdf size={14} weight="bold" /></button>
                      {(meta.is_admin || meta.is_ca) && <button onClick={() => onDelete(s.id)} className="p-1 text-red-600 hover:bg-red-50 rounded" title="Delete (galti se duplicate upload ho jaye)" data-testid={`bs-del-${s.id}`}><Trash size={14} /></button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <UploadDialog
        open={showUpload}
        accountKey={selectedAccount.key}
        accountLabel={selectedAccount.label}
        onClose={() => setShowUpload(false)}
        onUploaded={() => { loadStatements(selectedAccount.key); loadAccounts(); }}
      />
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────
 *  Upload dialog
 * ───────────────────────────────────────────────────────────── */
function UploadDialog({ open, onClose, onUploaded, accountKey, accountLabel }) {
  const [file, setFile] = useState(null);
  const [bankLabel, setBankLabel] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) return toast.error('File choose karein');
    if (!accountKey) return toast.error('Account select karein pehle');
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('bank_label', bankLabel);
      fd.append('account_key', accountKey);
      const { data } = await api.post('/bank-statements/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const ins = data.inserted ?? data.rows ?? 0;
      const skp = data.skipped ?? 0;
      toast.success(`✅ ${accountLabel} merge ho gaya: ${ins} naye rows added · ${skp} pehle se the (skipped)`);
      setFile(null); setBankLabel('');
      onClose(); onUploaded?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload fail');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="bs-upload-dialog">
        <DialogHeader><DialogTitle>📥 Upload to: {accountLabel}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="bg-blue-50 border border-blue-200 rounded p-2 text-[11px] text-blue-900">
            🏦 Account: <b>{accountLabel}</b> · Statement is account ke ledger me jayega.
          </div>
          <div>
            <label className="text-xs font-semibold">Optional sub-label (e.g., "Mar 2026")</label>
            <Input value={bankLabel} onChange={e => setBankLabel(e.target.value)} placeholder="optional" className="h-9 text-sm mt-1" data-testid="bs-bank-label" />
          </div>
          <div>
            <label className="text-xs font-semibold">CSV / Excel file *</label>
            <input type="file" accept=".csv,.xls,.xlsx" onChange={e => setFile(e.target.files?.[0] || null)} className="block w-full text-xs mt-1 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-emerald-100 file:text-emerald-800 file:font-semibold" data-testid="bs-file-input" />
            {file && <div className="text-[10px] text-gray-500 mt-1">📄 {file.name} ({(file.size / 1024).toFixed(1)} KB)</div>}
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[10px] text-amber-900">
            ✓ <b>Merge mode:</b> Daily/monthly upload kar do — system automatically duplicate skip karega<br />
            ✓ Pehle se claimed / remarks / suspense wala data SAFE rahega (kabhi reset nahi hoga)<br />
            ✓ Sirf naye rows blank dikhenge — purana kaam jaisa ka taisa<br />
            ✓ Auto-detect: Date, Narration, Credit/Debit columns automatically pehchaane jayenge<br />
            ✓ HDFC `.xls` (HTML format) support hai
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !file} className="bg-emerald-600 hover:bg-emerald-700" data-testid="bs-upload-submit">{busy ? 'Uploading…' : 'Upload & Parse'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ─────────────────────────────────────────────────────────────
 *  Admin access dialog: per-assistant toggle
 * ───────────────────────────────────────────────────────────── */
function AccessDialog({ open, onClose }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/bank-statements/access'); setList(data || []); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Load fail'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const toggle = async (uid, next) => {
    try {
      await api.patch(`/bank-statements/access/${uid}`, { enabled: next });
      toast.success(next ? '✅ Access enabled' : '🚫 Access disabled');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Toggle fail'); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="bs-access-dialog">
        <DialogHeader><DialogTitle>👥 Bank Statement Access — Assistants</DialogTitle></DialogHeader>
        <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[11px] text-amber-900 mb-2">
          💡 Sirf enabled assistants ko Bank Statement module dikhega. Default OFF rakha gaya hai. Admin & CA ko hamesha access milta hai.
        </div>
        <div className="max-h-80 overflow-y-auto border rounded">
          {loading ? <div className="text-center py-6 text-sm">Loading…</div> :
           list.length === 0 ? <div className="text-center py-6 text-sm text-gray-500">Koi assistant nahi mila</div> :
           list.map(u => (
            <div key={u.user_id} className="flex items-center justify-between px-3 py-2 border-b last:border-b-0 hover:bg-gray-50">
              <div>
                <div className="text-sm font-semibold">{u.name}</div>
                <div className="text-[11px] text-gray-500">{u.email}</div>
                {u.set_at && <div className="text-[10px] text-gray-400">last set by {u.set_by_name} · {formatDateTime(u.set_at)}</div>}
              </div>
              <button onClick={() => toggle(u.user_id, !u.enabled)} className="flex items-center gap-1.5" data-testid={`bs-access-toggle-${u.user_id}`}>
                {u.enabled
                  ? <><ToggleRight size={28} weight="fill" className="text-emerald-500" /><span className="text-xs text-emerald-700 font-bold">ENABLED</span></>
                  : <><ToggleLeft size={28} className="text-gray-400" /><span className="text-xs text-gray-500 font-medium">disabled</span></>}
              </button>
            </div>
          ))}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
